import React, { useEffect, useState } from 'react';
import {
  Box,
  CardContent,
  Button,
  Typography,
  Alert,
} from '@mui/material';
import { Lock } from '@mui/icons-material';
import Logo from '../common/Logo';
import { useAuthStore } from '../../store/authStore';
import { SetPasswordData } from '../../types/auth';
import ccLogo from '../../assets/images/cclogo.svg';
import { useSearchParams } from 'react-router-dom';
import {
  StyledCard,
  PasswordField,
  AuthErrorAlert,
  makeInputChangeHandler,
} from './_shared';
import { validatePasswordStrength, PASSWORD_RULES_HINT } from '../../utils/passwordValidation';

interface SetPasswordFormProps {
  onSwitchToLogin: () => void;
}

const SetPasswordForm: React.FC<SetPasswordFormProps> = ({onSwitchToLogin }) => {
  const [searchParams] = useSearchParams();

  const { resetPassword, isLoading, error, clearError } = useAuthStore();

  const [formData, setFormData] = useState<SetPasswordData>({
    token: '',
    password: '',
    confirmPassword: '',
  });

  const [message, setMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRePassword, setShowRePassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const token = searchParams.get('token');
    if(token) {
      setFormData({
        token: token,
        password: '',
        confirmPassword: '',
      });
    } else {
      onSwitchToLogin();
    }
  }, [searchParams]);

  const handleInputChange = (field: keyof SetPasswordData) =>
    makeInputChangeHandler(field, setFormData, validationErrors, setValidationErrors, error, clearError);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.password) {
      errors.password = 'Password is required';
    } else {
      const strengthError = validatePasswordStrength(formData.password);
      if (strengthError) {
        errors.password = strengthError;
      }
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Confirm password is required';
    } else if (formData.confirmPassword !== formData.password) {
      errors.confirmPassword = 'Confirm password does not match password';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const result = await resetPassword(formData);

    // Normal login success
    if (result === true) {
      setMessage('Password updated successfully.')
      setTimeout(() => {
        onSwitchToLogin()
      }, 1500);
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const toggleShowRePassword = () => {
    setShowRePassword(!showRePassword);
  };

  return (
    <StyledCard>
      <CardContent sx={{ p: 5 }}>
        {/* Logo and Header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 4,
            }}
          >
            <Logo src={ccLogo} width={280} style={{ marginTop: 15, marginBottom: 15 }} />
          </Box>

          <Typography
            variant="h5"
            component="h1"
            sx={{
              fontWeight: 700,
              color: 'grey.800',
              letterSpacing: '-0.01em',
              mb: 1,
            }}
          >
            Set Your Password
          </Typography>

        </Box>

        {/* Success Alert */}
        {message && (
          <Alert
            severity="success"
            sx={{
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-icon': {
                fontSize: '1.5rem',
              },
            }}
          >
            {message}
          </Alert>
        )}

        {/* Error Alert */}
        <AuthErrorAlert message={error} />

        {/* Login Form */}
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <PasswordField
            fullWidth
            label="Password"
            value={formData.password}
            onChange={handleInputChange('password')}
            error={!!validationErrors.password}
            helperText={validationErrors.password || PASSWORD_RULES_HINT}
            disabled={isLoading}
            margin="normal"
            placeholder="Enter your password"
            showPassword={showPassword}
            onToggleShow={toggleShowPassword}
            isLoading={isLoading}
            iconOrder="show-when-shown"
            styledToggle
            startAdornment={<Lock sx={{ color: 'grey.500', fontSize: '1.25rem' }} />}
            sx={{ mb: 2 }}
          />

          <PasswordField
            fullWidth
            label="Confirm Password"
            value={formData.confirmPassword}
            onChange={handleInputChange('confirmPassword')}
            error={!!validationErrors.confirmPassword}
            helperText={validationErrors.confirmPassword}
            disabled={isLoading}
            margin="normal"
            placeholder="Re-enter your password"
            showPassword={showRePassword}
            onToggleShow={toggleShowRePassword}
            isLoading={isLoading}
            iconOrder="show-when-shown"
            styledToggle
            startAdornment={<Lock sx={{ color: 'grey.500', fontSize: '1.25rem' }} />}
            sx={{ mb: 2 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isLoading}
            sx={{ mt: 3, mb: 2, py: 1.5 }}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </Button>

        </Box>

        {/* Footer */}
        <Box
          sx={{
            mt: 4,
            pt: 3,
            borderTop: '1px solid',
            borderColor: 'grey.200',
            textAlign: 'center',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.75rem',
            }}
          >
            Protected by enterprise-grade security
          </Typography>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default SetPasswordForm;
